import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  take,
  timer,
} from 'rxjs';

import { GraphData, InputData, InputDetails } from '../../util/calculation.interface';
import {
  BetrCalculateRequest,
  BetrCalculationService,
} from '../betr-calculation/betr-calculation.service';
import { CalculationService } from '../calculation/calculation.service';
import { GatekeeperService } from '../gatekeeper/gatekeeper.service';
import { TaxRateBracket, TaxRates, TaxRatesService } from '../tax-rates/tax-rates.service';

// A call that resolves faster than this never shows the chart mask, so warm-lambda
// responses don't flash it.
export const LOADING_REVEAL_DELAY_MS = 500;

export const defaultClientInfo: InputData = {
  accountType: 'IRA',

  currentAnnualIncome: 150000,
  currentFilingStatus: 'single',
  currentFederalTaxRate: 24.0,
  currentStateTaxRate: 0.0,
  currentStateOfTaxation: 'NC',
  currentStateTaxStatic: 0,
  currentLocalTax: 0,
  futureAnnualIncome: 100000,
  futureFilingStatus: 'single',
  futureFederalTaxRate: 22.0,
  futureStateTaxRate: 0.0,
  futureStateOfTaxation: 'NC',
  futureStateTaxStatic: 0,
  futureLocalTax: 0,

  rothConversionAmount: 50000,
  yearsUntilDist: 20,
  howWillYouPay: 'cash',
  percentOfIra: 0,

  dividendTaxRate: 15,
  capitalGainsTaxRate: 15,
  annualReturn: 6,
  dividendYield: 2,
  cashYield: 2,
};

const paulData: InputData = {
  accountType: 'IRA',
  currentAnnualIncome: 800000,
  currentFilingStatus: 'single',
  currentFederalTaxRate: 37.0,
  currentStateTaxRate: 4.0,
  currentStateOfTaxation: 'NC',
  currentStateTaxStatic: 0,
  currentLocalTax: 0,
  futureAnnualIncome: 150000,
  futureFilingStatus: 'single',
  futureFederalTaxRate: 24.0,
  futureStateTaxRate: 4.0,
  futureStateOfTaxation: 'NC',
  futureStateTaxStatic: 0,
  futureLocalTax: 0,
  rothConversionAmount: 50000,
  yearsUntilDist: 20,
  howWillYouPay: 'tax-inefficient',
  percentOfIra: 0,
  dividendTaxRate: 23.8,
  capitalGainsTaxRate: 15,
  annualReturn: 6,
  dividendYield: 2,
  cashYield: 2,
};

const fionaData: InputData = {
  accountType: 'IRA',
  currentAnnualIncome: 180000,
  currentFilingStatus: 'single',
  currentFederalTaxRate: 24.0,
  currentStateTaxRate: 4.0,
  currentStateOfTaxation: 'NC',
  currentStateTaxStatic: 0,
  currentLocalTax: 0,
  futureAnnualIncome: 300000,
  futureFilingStatus: 'single',
  futureFederalTaxRate: 35.0,
  futureStateTaxRate: 4.0,
  futureStateOfTaxation: 'NC',
  futureStateTaxStatic: 0,
  futureLocalTax: 0,
  rothConversionAmount: 50000,
  yearsUntilDist: 25,
  howWillYouPay: 'cash',
  percentOfIra: 50,
  dividendTaxRate: 15,
  capitalGainsTaxRate: 18.8,
  annualReturn: 6,
  dividendYield: 2,
  cashYield: 2,
};

const steadySaverData: InputData = {
  accountType: 'IRA',
  currentAnnualIncome: 80000,
  currentFilingStatus: 'married-filing-jointly',
  currentFederalTaxRate: 12.0,
  currentStateTaxRate: 4.0,
  currentStateOfTaxation: 'NC',
  currentStateTaxStatic: 0,
  currentLocalTax: 0,
  futureAnnualIncome: 150000,
  futureFilingStatus: 'married-filing-jointly',
  futureFederalTaxRate: 22.0,
  futureStateTaxRate: 4.0,
  futureStateOfTaxation: 'NC',
  futureStateTaxStatic: 0,
  futureLocalTax: 0,
  rothConversionAmount: 50000,
  yearsUntilDist: 15,
  howWillYouPay: 'tax-efficient',
  percentOfIra: 0,
  dividendTaxRate: 0,
  capitalGainsTaxRate: 15,
  annualReturn: 6,
  dividendYield: 2,
  cashYield: 2,
};

const legacyPlannersData: InputData = {
  accountType: 'IRA',
  currentAnnualIncome: 250000,
  currentFilingStatus: 'married-filing-jointly',
  currentFederalTaxRate: 24.0,
  currentStateTaxRate: 4.0,
  currentStateOfTaxation: 'NC',
  currentStateTaxStatic: 0,
  currentLocalTax: 0,
  futureAnnualIncome: 500000,
  futureFilingStatus: 'single',
  futureFederalTaxRate: 35.0,
  futureStateTaxRate: 4.0,
  futureStateOfTaxation: 'NC',
  futureStateTaxStatic: 0,
  futureLocalTax: 0,
  rothConversionAmount: 100000,
  yearsUntilDist: 30,
  howWillYouPay: 'cash',
  percentOfIra: 0,
  dividendTaxRate: 18.8,
  capitalGainsTaxRate: 18.8,
  annualReturn: 6,
  dividendYield: 2,
  cashYield: 2,
};

export type InputSection = 'clientTax' | 'rothConversion';

/** The four rate fields BXR can derive itself, and so the four it accepts overrides for. */
export type RateField = 'currentFederal' | 'currentState' | 'futureFederal' | 'futureState';

// Keyed by the setter that fills each field, so any write - typed, picked or auto-filled from
// another field - counts as the field being entered again.
export type ClearableInput =
  | 'currentIncome'
  | 'currentFilingStatus'
  | 'currentFederalTaxRate'
  | 'currentStateTaxRate'
  | 'currentStateOfTaxation'
  | 'currentStateTaxStatic'
  | 'currentLocalTax'
  | 'futureIncome'
  | 'futureFilingStatus'
  | 'futureFederalTaxRate'
  | 'futureStateTaxRate'
  | 'futureStateOfTaxation'
  | 'futureStateTaxStatic'
  | 'futureLocalTax'
  | 'rothConversionAmount'
  | 'yearsUntilDist'
  | 'howWillYouPay'
  | 'annualReturn'
  | 'percentOfIra';

// Must match the names the section's components pass to addInputError, or clearing a section
// leaves its errors behind.
const SECTION_INPUT_ERRORS: Record<InputSection, readonly string[]> = {
  clientTax: [
    'Annual taxable income after deductions',
    'Federal tax rate',
    'State & local tax rate',
    'Taxable income at distribution',
  ],
  rothConversion: [
    'Roth conversion amount',
    'Years until distribution',
    'Annual return',
    'Cash yield',
    'Dividend tax rate',
    'Capital gains tax rate',
    'Dividend yield',
    'Percent of IRA already taxed',
  ],
};

@Injectable({
  providedIn: 'root',
})
export class SharedInfoService {
  sampleInputData: InputData[] = [
    { ...defaultClientInfo },
    { ...paulData },
    { ...fionaData },
    { ...steadySaverData },
    { ...legacyPlannersData },
  ];

  private sampleClientIndexSubject: BehaviorSubject<number> = new BehaviorSubject(0);
  private sampleClientLoadedSubject: ReplaySubject<void> = new ReplaySubject<void>(1);
  private sampleClientModifiedSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private isLoadingSampleClientData = false;

  get defaultInputData(): InputData {
    return this.sampleInputData[this.sampleClientIndexSubject.getValue()];
  }

  private accountTypeSubject: BehaviorSubject<string>;

  private currentIncomeSubject: BehaviorSubject<number>;
  private filingStatusSubject: BehaviorSubject<string>;
  private currentFederalTaxRateSubject: BehaviorSubject<number>;
  private currentStateTaxRateSubject: BehaviorSubject<number>;
  private currentStateOfTaxationSubject: BehaviorSubject<string>;
  private currentStateTaxStaticSubject: BehaviorSubject<number>;
  private currentLocalTaxSubject: BehaviorSubject<number>;
  private futureIncomeSubject: BehaviorSubject<number>;
  private futureFilingStatusSubject: BehaviorSubject<string>;
  private futureFederalTaxRateSubject: BehaviorSubject<number>;
  private futureStateTaxRateSubject: BehaviorSubject<number>;
  private futureStateOfTaxationSubject: BehaviorSubject<string>;
  private futureStateTaxStaticSubject: BehaviorSubject<number>;
  private futureLocalTaxSubject: BehaviorSubject<number>;
  private futureTaxRateSubject: BehaviorSubject<number>;

  private rothConversionAmountSubject: BehaviorSubject<number>;
  private yearsUntilDistSubject: BehaviorSubject<number>;
  private howWillYouPaySubject: BehaviorSubject<string>;
  private percentOfIraSubject: BehaviorSubject<number>;

  private taxChartDividendTaxRateSubject: BehaviorSubject<number>;
  private taxChartCapitalGainsTaxRateSubject: BehaviorSubject<number>;

  private dividendTaxRateSubject: BehaviorSubject<number>;
  private capitalGainsTaxRateSubject: BehaviorSubject<number>;
  private annualReturnSubject: BehaviorSubject<number>;
  private dividendYieldSubject: BehaviorSubject<number>;
  private cashYieldSubject: BehaviorSubject<number>;

  private graphDataSubject: BehaviorSubject<GraphData>;
  private inputsValidSubject: BehaviorSubject<boolean>;

  // These mirror currentIncome / futureIncome / rothConversionAmount, but are published on every change
  // and coerced to 0 while a field is blank or in error. The subjects above deliberately keep the last
  // known-good value for the graph, BETR calc and PDF; these drive tax-bracket selection and the
  // combined-income helper text, which must treat an unusable field as zero.
  private effectiveCurrentIncomeSubject: BehaviorSubject<number>;
  private effectiveFutureIncomeSubject: BehaviorSubject<number>;
  private effectiveRothConversionAmountSubject: BehaviorSubject<number>;

  private combinedCurrentIncomeSubject: BehaviorSubject<number>;
  private combinedFutureIncomeSubject: BehaviorSubject<number>;

  private inputsErrorList: string[] = [];

  // Fields blanked by Clear all fields and not yet re-entered. Their subjects keep the last known-good
  // value, so the chart is held empty rather than calculated from values the user no longer sees.
  private readonly clearedInputs = new Set<ClearableInput>();
  // Stays set after the last field is re-entered until a fresh calculation lands, so the chart doesn't
  // briefly redraw the pre-clear result.
  private awaitingRecalculation = false;
  private readonly inputsCompleteSubject = new BehaviorSubject<boolean>(true);
  private readonly inputsClearedSubject = new Subject<InputSection>();

  private readonly graphDataTrigger = new Subject<void>();
  // Fires the chart calculation without the typing debounce, for triggers that are already
  // settled — a dropdown click, or freshly arrived tax rates.
  private readonly graphDataNowTrigger = new Subject<void>();

  // Live TXS-backed brackets and flags, one set per scenario since each has its own state
  // and filing status. Null until the first fetch resolves; consumers fall back to safe
  // defaults rather than showing a wrong rate or tax claim.
  private readonly currentTaxRatesSubject = new BehaviorSubject<TaxRates | null>(null);
  private readonly futureTaxRatesSubject = new BehaviorSubject<TaxRates | null>(null);
  private readonly taxRatesTrigger = new Subject<void>();

  // The TXS-derived default each rate field was filled with. Kept here, not in the form component,
  // because desktop and the mobile sheet each render their own copy of that component while the
  // BXR request is built once, here. A rate that differs from its default goes to BXR as an override.
  private readonly rateDefaults: Record<RateField, number | null> = {
    currentFederal: null,
    currentState: null,
    futureFederal: null,
    futureState: null,
  };

  // Either network call (TXS tax rates or the BETR calculation) masks the chart. The mask
  // covers only the chart, not the inputs, so a recalculation while typing doesn't block entry.
  private taxRatesInFlight = false;
  private calculationInFlight = false;
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);

  // eslint-disable-next-line max-params
  constructor(
    private readonly gatekeeperService: GatekeeperService,
    private readonly betrCalculationService: BetrCalculationService,
    private readonly calculationService: CalculationService,
    private readonly taxRatesService: TaxRatesService,
  ) {
    this.taxRatesTrigger
      .pipe(
        // A state or filing-status change is one deliberate click, not typing, so it needs
        // only enough delay to coalesce the pair of scenario updates that arrive together.
        debounceTime(50),
        switchMap(() => {
          if (!this.gatekeeperService.getToggleValue('STATE_TAX_RATES')) {
            return of(null);
          }
          return Promise.all([this.fetchTaxRates(false), this.fetchTaxRates(true)]).catch((error) => {
            console.error('Tax rates fetch failed', error);
            return null;
          });
        }),
      )
      .subscribe((taxRates) => {
        this.taxRatesInFlight = false;
        // A fetch already in flight when the fields were cleared would refill them from the old lookup.
        const current = taxRates && !this.taxLookupCleared(false) ? taxRates[0] : null;
        const future = taxRates && !this.taxLookupCleared(true) ? taxRates[1] : null;
        if (current) {
          this.currentTaxRatesSubject.next(current);
        }
        if (future) {
          this.futureTaxRatesSubject.next(future);
        }
        if (current || future) {
          // Bracket-derived defaults change with the new data, so refresh the fields they feed.
          this.setUpdatedDividendTaxRateBasedOnCurrentYear();
          this.setUpdatedCapitalGainsTaxRateBasedOnCurrentYear();
          // Those refreshed rates feed the chart; recalculate immediately rather than sitting
          // through another debounce, since nothing further is coming.
          this.graphDataNowTrigger.next();
        }
        // Published after the recalculation starts, so loading doesn't dip to false between
        // the fetch and the calculation it feeds, which would restart the reveal delay.
        this.publishLoading();
      });

    // The request last sent to BXR, so a trigger that changes nothing doesn't send it again: a
    // state change fires the fast lane once its tax rates land and the debounced lane 400ms later
    // from the rate fields those rates refill, both with an identical request. A failed call is
    // forgotten so the same inputs can retry.
    let lastSentRequest: string | null = null;
    merge(this.graphDataTrigger.pipe(debounceTime(400)), this.graphDataNowTrigger)
      .pipe(
        filter(() => {
          if (this.clearedInputs.size > 0 || !this.gatekeeperService.getToggleValue('STATE_TAX_RATES')) {
            return true;
          }
          const request = JSON.stringify(this.betrRequest);
          if (request === lastSentRequest) {
            return false;
          }
          lastSentRequest = request;
          return true;
        }),
        // switchMap cancels an in-flight calculation, so the fast lane racing the debounced
        // one is safe: the later emission wins and only its result is published.
        switchMap(() => {
          if (this.clearedInputs.size > 0) {
            // Cleared fields hold the chart; once refilled, the same request must go out again.
            lastSentRequest = null;
            return of(null);
          }
          if (!this.gatekeeperService.getToggleValue('STATE_TAX_RATES')) {
            return of(this.localGraphData());
          }
          this.calculationInFlight = true;
          this.publishLoading();
          return this.betrCalculationService
            .calculate(this.betrRequest)
            .catch((error) => {
              console.error('BETR calculation failed', error);
              lastSentRequest = null;
              return null;
            });
        }),
      )
      .subscribe((graphData) => {
        // switchMap drops superseded calculations, so this only runs for the latest one.
        this.calculationInFlight = false;
        this.publishLoading();
        if (graphData) {
          this.graphDataSubject.next(graphData);
          if (this.awaitingRecalculation && this.clearedInputs.size === 0) {
            this.awaitingRecalculation = false;
            this.publishInputsComplete();
          }
        }
      });
  }

  private publishLoading(): void {
    const inFlight = this.taxRatesInFlight || this.calculationInFlight;
    if (inFlight !== this.loadingSubject.getValue()) {
      this.loadingSubject.next(inFlight);
    }
  }

  /**
   * True once a tax-rates fetch or BETR calculation has been outstanding for
   * LOADING_REVEAL_DELAY_MS. Faster calls never show the mask; clearing is immediate.
   */
  getLoading$(): Observable<boolean> {
    return this.loadingSubject.pipe(
      switchMap((inFlight) =>
        inFlight ? timer(LOADING_REVEAL_DELAY_MS).pipe(map(() => true)) : of(false),
      ),
      distinctUntilChanged(),
    );
  }

  // eslint-disable-next-line max-statements
  async initializeService() {
    this.sampleClientIndexSubject.next(0);
    this.sampleClientModifiedSubject.next(false);

    const defaultInputData: InputData = this.sampleInputData[0];

    this.accountTypeSubject = new BehaviorSubject<string>(defaultInputData.accountType);

    this.currentIncomeSubject = new BehaviorSubject<number>(defaultInputData.currentAnnualIncome);
    this.filingStatusSubject = new BehaviorSubject<string>(defaultInputData.currentFilingStatus);
    this.currentFederalTaxRateSubject = new BehaviorSubject<number>(
      defaultInputData.currentFederalTaxRate,
    );
    this.currentStateTaxRateSubject = new BehaviorSubject<number>(
      defaultInputData.currentStateTaxRate,
    );
    this.currentStateOfTaxationSubject = new BehaviorSubject<string>(
      defaultInputData.currentStateOfTaxation,
    );
    this.currentStateTaxStaticSubject = new BehaviorSubject<number>(
      defaultInputData.currentStateTaxStatic,
    );
    this.currentLocalTaxSubject = new BehaviorSubject<number>(defaultInputData.currentLocalTax);

    this.futureIncomeSubject = new BehaviorSubject<number>(defaultInputData.futureAnnualIncome);
    this.futureFilingStatusSubject = new BehaviorSubject<string>(
      defaultInputData.futureFilingStatus,
    );
    this.futureFederalTaxRateSubject = new BehaviorSubject<number>(
      defaultInputData.futureFederalTaxRate,
    );
    this.futureStateTaxRateSubject = new BehaviorSubject<number>(
      defaultInputData.futureStateTaxRate,
    );
    this.futureStateOfTaxationSubject = new BehaviorSubject<string>(
      defaultInputData.futureStateOfTaxation,
    );
    this.futureStateTaxStaticSubject = new BehaviorSubject<number>(
      defaultInputData.futureStateTaxStatic,
    );
    this.futureLocalTaxSubject = new BehaviorSubject<number>(defaultInputData.futureLocalTax);
    this.futureTaxRateSubject = new BehaviorSubject<number>(defaultInputData.futureFederalTaxRate);

    this.rothConversionAmountSubject = new BehaviorSubject<number>(
      defaultInputData.rothConversionAmount,
    );
    this.yearsUntilDistSubject = new BehaviorSubject<number>(defaultInputData.yearsUntilDist);
    this.howWillYouPaySubject = new BehaviorSubject<string>(defaultInputData.howWillYouPay);
    this.percentOfIraSubject = new BehaviorSubject<number>(defaultInputData.percentOfIra);

    this.taxChartDividendTaxRateSubject = new BehaviorSubject<number>(
      defaultInputData.dividendTaxRate,
    );
    this.taxChartCapitalGainsTaxRateSubject = new BehaviorSubject<number>(
      defaultInputData.capitalGainsTaxRate,
    );

    this.dividendTaxRateSubject = new BehaviorSubject<number>(defaultInputData.dividendTaxRate);
    this.capitalGainsTaxRateSubject = new BehaviorSubject<number>(
      defaultInputData.capitalGainsTaxRate,
    );
    this.annualReturnSubject = new BehaviorSubject<number>(defaultInputData.annualReturn);
    this.dividendYieldSubject = new BehaviorSubject<number>(defaultInputData.dividendYield);
    this.cashYieldSubject = new BehaviorSubject<number>(defaultInputData.cashYield);

    // Must be seeded before graphDataSubject below, which reads the bracket functions.
    this.effectiveCurrentIncomeSubject = new BehaviorSubject<number>(
      defaultInputData.currentAnnualIncome,
    );
    this.effectiveFutureIncomeSubject = new BehaviorSubject<number>(
      defaultInputData.futureAnnualIncome,
    );
    this.effectiveRothConversionAmountSubject = new BehaviorSubject<number>(
      defaultInputData.rothConversionAmount,
    );
    this.combinedCurrentIncomeSubject = new BehaviorSubject<number>(
      this.getIncomeForBrackets(false),
    );
    this.combinedFutureIncomeSubject = new BehaviorSubject<number>(this.getIncomeForBrackets(true));

    this.graphDataSubject = new BehaviorSubject<GraphData>(this.graphData());
    this.inputsValidSubject = new BehaviorSubject<boolean>(true);

    // Subjects now exist, so the fetch can read the default state and filing status.
    this.setTaxRates();
  }

  get currentInputData(): InputData {
    return {
      accountType: this.accountTypeSubject.getValue(),

      currentAnnualIncome: this.currentIncomeSubject.getValue(),
      currentFilingStatus: this.filingStatusSubject.getValue(),
      currentFederalTaxRate: this.currentFederalTaxRateSubject.getValue(),
      currentStateTaxRate: this.currentStateTaxRateSubject.getValue(),
      currentStateOfTaxation: this.currentStateOfTaxationSubject.getValue(),
      currentStateTaxStatic: this.currentStateTaxStaticSubject.getValue(),
      currentLocalTax: this.currentLocalTaxSubject.getValue(),
      futureAnnualIncome: this.futureIncomeSubject.getValue(),
      futureFilingStatus: this.futureFilingStatusSubject.getValue(),
      futureFederalTaxRate: this.futureFederalTaxRateSubject.getValue(),
      futureStateTaxRate: this.futureStateTaxRateSubject.getValue(),
      futureStateOfTaxation: this.futureStateOfTaxationSubject.getValue(),
      futureStateTaxStatic: this.futureStateTaxStaticSubject.getValue(),
      futureLocalTax: this.futureLocalTaxSubject.getValue(),

      rothConversionAmount: this.rothConversionAmountSubject.getValue(),
      yearsUntilDist: this.yearsUntilDistSubject.getValue(),
      howWillYouPay: this.howWillYouPaySubject.getValue(),
      percentOfIra: this.percentOfIraSubject.getValue(),

      dividendTaxRate: this.dividendTaxRateSubject.getValue(),
      capitalGainsTaxRate: this.capitalGainsTaxRateSubject.getValue(),
      annualReturn: this.annualReturnSubject.getValue(),
      dividendYield: this.dividendYieldSubject.getValue(),
      cashYield: this.cashYieldSubject.getValue(),
    };
  }

  // Seed for graphDataSubject. Behind the STATE_TAX_RATES toggle: computed
  // synchronously via the old local CalculationService. Ahead of it: zeroed,
  // since the real values arrive once the first BETR Lambda call resolves.
  private graphData(): GraphData {
    if (!this.gatekeeperService.getToggleValue('STATE_TAX_RATES')) {
      return this.localGraphData();
    }
    const zeroedYearRange = Array.from({ length: 51 }, (_, year) => ({ year, value: 0 }));
    return {
      assetGrowthData: { conversionLineData: zeroedYearRange, nonConversionLineData: zeroedYearRange },
      breakEvenData: zeroedYearRange,
    };
  }

  private localGraphData(): GraphData {
    return {
      assetGrowthData: this.calculationService.selectedAssetGrowthData(this.inputDetails),
      breakEvenData: this.calculationService.selectedBreakEvenTaxRateData(this.inputDetails),
    };
  }

  setGraphData() {
    this.graphDataTrigger.next();
    if (!this.isLoadingSampleClientData) {
      this.sampleClientModifiedSubject.next(this.hasModifiedSampleClientData());
    }
  }

  private hasModifiedSampleClientData(): boolean {
    if (this.clearedInputs.size > 0) {
      return true;
    }
    const sampleData = this.sampleInputData[this.getSampleClientIndex()];
    const currentData = this.currentInputData;
    return (Object.keys(sampleData) as (keyof InputData)[]).some(
      (key) => sampleData[key] !== currentData[key],
    );
  }

  getGraphData$(): Observable<GraphData> {
    return this.graphDataSubject.asObservable();
  }

  getInputsValid$(): Observable<boolean> {
    return this.inputsValidSubject.asObservable();
  }

  getSampleClientIndex(): number {
    return this.sampleClientIndexSubject.getValue();
  }

  getSampleClientIndex$(): Observable<number> {
    return this.sampleClientIndexSubject.asObservable();
  }

  getSampleClientLoaded$(): Observable<void> {
    return this.sampleClientLoadedSubject.asObservable();
  }

  getSampleClientModified$(): Observable<boolean> {
    return this.sampleClientModifiedSubject.asObservable();
  }

  setSampleClientIndex(index: number): void {
    if (index < 0 || index >= this.sampleInputData.length) return;
    this.sampleClientIndexSubject.next(index);
    this.loadSampleClientData(index);
  }

  loadSampleClientData(index: number = this.getSampleClientIndex()): void {
    this.applyInputData(this.sampleInputData[index]);
  }

  // eslint-disable-next-line max-statements
  private applyInputData(sampleData: InputData): void {
    // Emitting the tax chart rates below re-enters the field setters via RothConvDetailsComponent,
    // so suppress the modified check until every field has been written.
    this.isLoadingSampleClientData = true;

    // Clear all input errors when loading a sample client
    this.inputsErrorList = [];
    // Every field is written below, so nothing is left blank.
    this.clearedInputs.clear();
    this.awaitingRecalculation = false;
    this.publishInputsComplete();

    this.accountTypeSubject.next(sampleData.accountType);

    this.currentIncomeSubject.next(sampleData.currentAnnualIncome);
    this.filingStatusSubject.next(sampleData.currentFilingStatus);
    this.currentFederalTaxRateSubject.next(sampleData.currentFederalTaxRate);
    this.currentStateTaxRateSubject.next(sampleData.currentStateTaxRate);
    this.currentStateOfTaxationSubject.next(sampleData.currentStateOfTaxation);
    this.currentStateTaxStaticSubject.next(sampleData.currentStateTaxStatic);
    this.currentLocalTaxSubject.next(sampleData.currentLocalTax);

    this.futureIncomeSubject.next(sampleData.futureAnnualIncome);
    this.futureFilingStatusSubject.next(sampleData.futureFilingStatus);
    this.futureFederalTaxRateSubject.next(sampleData.futureFederalTaxRate);
    this.futureStateTaxRateSubject.next(sampleData.futureStateTaxRate);
    this.futureStateOfTaxationSubject.next(sampleData.futureStateOfTaxation);
    this.futureStateTaxStaticSubject.next(sampleData.futureStateTaxStatic);
    this.futureLocalTaxSubject.next(sampleData.futureLocalTax);
    this.setFutureTaxRate();

    this.rothConversionAmountSubject.next(sampleData.rothConversionAmount);
    this.yearsUntilDistSubject.next(sampleData.yearsUntilDist);
    this.howWillYouPaySubject.next(sampleData.howWillYouPay);
    this.percentOfIraSubject.next(sampleData.percentOfIra);

    this.taxChartDividendTaxRateSubject.next(sampleData.dividendTaxRate);
    this.taxChartCapitalGainsTaxRateSubject.next(sampleData.capitalGainsTaxRate);

    this.dividendTaxRateSubject.next(sampleData.dividendTaxRate);
    this.capitalGainsTaxRateSubject.next(sampleData.capitalGainsTaxRate);
    this.annualReturnSubject.next(sampleData.annualReturn);
    this.dividendYieldSubject.next(sampleData.dividendYield);
    this.cashYieldSubject.next(sampleData.cashYield);

    // Sample data bypasses the component change handlers, so the effective values have to be written
    // here too. Sample data is always valid, so they mirror it exactly. Recomputing before the loaded
    // signal below means subscribers read correct combined figures on their first notification.
    this.effectiveCurrentIncomeSubject.next(sampleData.currentAnnualIncome);
    this.effectiveFutureIncomeSubject.next(sampleData.futureAnnualIncome);
    this.effectiveRothConversionAmountSubject.next(sampleData.rothConversionAmount);
    this.recomputeCombinedIncome();

    this.isLoadingSampleClientData = false;
    this.setGraphData();

    // Emit event to notify components that a sample client has been loaded, once every field is set
    this.sampleClientLoadedSubject.next();
  }

  setAccountType(accountType: string): void {
    this.accountTypeSubject.next(accountType);
    this.setGraphData();
  }

  getAccountType$(): Observable<string> {
    return this.accountTypeSubject.asObservable();
  }

  setCurrentIncome(currentIncome: number): void {
    this.currentIncomeSubject.next(currentIncome);
    this.markEntered('currentIncome');
    this.setGraphData();
  }

  getCurrentIncome$(): Observable<number> {
    return this.currentIncomeSubject.asObservable();
  }

  setFilingStatus(filingStatus: string): void {
    this.filingStatusSubject.next(filingStatus);
    this.markEntered('currentFilingStatus');
    this.setTaxRates();
    this.setGraphData();
  }

  getFilingStatus$(): Observable<string> {
    return this.filingStatusSubject.asObservable();
  }

  setCurrentFederalTaxRate(currentIncome: number): void {
    this.currentFederalTaxRateSubject.next(currentIncome);
    this.markEntered('currentFederalTaxRate');
    this.setGraphData();
  }

  getCurrentFederalTaxRate$(): Observable<number> {
    return this.currentFederalTaxRateSubject.asObservable();
  }

  setCurrentStateTaxRate(currentIncome: number): void {
    this.currentStateTaxRateSubject.next(currentIncome);
    this.markEntered('currentStateTaxRate');
    this.setGraphData();
  }

  getCurrentStateTaxRate$(): Observable<number> {
    return this.currentStateTaxRateSubject.asObservable();
  }

  setCurrentStateOfTaxation(currentStateOfTaxation: string): void {
    this.currentStateOfTaxationSubject.next(currentStateOfTaxation);
    this.markEntered('currentStateOfTaxation');
    this.setTaxRates();
  }

  getCurrentStateOfTaxation$(): Observable<string> {
    return this.currentStateOfTaxationSubject.asObservable();
  }

  setCurrentStateTaxStatic(currentStateTaxStatic: number): void {
    this.currentStateTaxStaticSubject.next(currentStateTaxStatic);
    this.markEntered('currentStateTaxStatic');
  }

  getCurrentStateTaxStatic$(): Observable<number> {
    return this.currentStateTaxStaticSubject.asObservable();
  }

  setCurrentLocalTax(currentLocalTax: number): void {
    this.currentLocalTaxSubject.next(currentLocalTax);
    this.markEntered('currentLocalTax');
  }

  getCurrentLocalTax$(): Observable<number> {
    return this.currentLocalTaxSubject.asObservable();
  }

  setFutureIncome(futureIncome: number): void {
    this.futureIncomeSubject.next(futureIncome);
    this.markEntered('futureIncome');
    this.setGraphData();
  }

  getFutureIncome$(): Observable<number> {
    return this.futureIncomeSubject.asObservable();
  }

  /**
   * Income used to select a federal tax bracket. Behind the STATE_TAX_RATES toggle the CURRENT scenario
   * adds the Roth conversion amount, since the conversion is ordinary income in the year it happens.
   * The FUTURE scenario uses future income alone — the conversion isn't income at distribution.
   * With the toggle off this returns the last known-good income alone, preserving the original behavior.
   */
  private getIncomeForBrackets(isFuture: boolean): number {
    if (!this.gatekeeperService.getToggleValue('STATE_TAX_RATES')) {
      return isFuture ? this.futureIncomeSubject.getValue() : this.currentIncomeSubject.getValue();
    }

    if (isFuture) {
      return this.effectiveFutureIncomeSubject.getValue();
    }
    return (
      this.effectiveCurrentIncomeSubject.getValue() +
      this.effectiveRothConversionAmountSubject.getValue()
    );
  }

  /**
   * Income alone, excluding the Roth conversion amount — unlike getIncomeForBrackets. Used for
   * the dividend/capital-gains rate (bracket lookup AND NIIT threshold, confirmed with the
   * business 2026-10-01) and for the NIIT threshold specifically wherever else it's checked.
   * The Federal tax rate field and the "until next bracket" hint are NOT affected by this and
   * still use getIncomeForBrackets' combined income.
   */
  private getIncomeExcludingConversion(isFuture: boolean): number {
    if (!this.gatekeeperService.getToggleValue('STATE_TAX_RATES')) {
      return isFuture ? this.futureIncomeSubject.getValue() : this.currentIncomeSubject.getValue();
    }
    return isFuture
      ? this.effectiveFutureIncomeSubject.getValue()
      : this.effectiveCurrentIncomeSubject.getValue();
  }

  private recomputeCombinedIncome(): void {
    this.combinedCurrentIncomeSubject.next(this.getIncomeForBrackets(false));
    this.combinedFutureIncomeSubject.next(this.getIncomeForBrackets(true));
  }

  /** Requests a refresh of both scenarios' tax data. Debounced; safe to call on every change. */
  setTaxRates(): void {
    // Only network work masks the chart; with the toggle off nothing is fetched.
    if (this.gatekeeperService.getToggleValue('STATE_TAX_RATES')) {
      this.taxRatesInFlight = true;
      this.publishLoading();
    }
    this.taxRatesTrigger.next();
  }

  getCurrentTaxRates$(): Observable<TaxRates | null> {
    return this.currentTaxRatesSubject.asObservable();
  }

  getFutureTaxRates$(): Observable<TaxRates | null> {
    return this.futureTaxRatesSubject.asObservable();
  }

  private taxRatesFor(isFuture: boolean): TaxRates | null {
    return isFuture ? this.futureTaxRatesSubject.getValue() : this.currentTaxRatesSubject.getValue();
  }

  /** The bracket an income falls in. maxIncome is null only on the open-ended top bracket. */
  private bracketFor(brackets: TaxRateBracket[], income: number): TaxRateBracket | null {
    return brackets.find((b) => b.maxIncome === null || income <= b.maxIncome) ?? null;
  }

  /**
   * Dollars until the next bracket starts, or null when already in the highest one — the UI
   * shows "at the highest federal bracket" instead of an amount.
   */
  private amountUntilNextBracket(brackets: TaxRateBracket[], income: number): number | null {
    const bracket = this.bracketFor(brackets, income);
    if (!bracket || bracket.maxIncome === null) {
      return null;
    }
    return bracket.maxIncome + 1 - income;
  }

  /**
   * Investment-income rate as a percentage: the capital gains bracket for income, plus NIIT
   * once income reaches its threshold. This is what produces the 18.8% / 23.8% values the UI
   * shows.
   *
   * Confirmed with the business (2026-10-01): both the bracket lookup and the NIIT threshold
   * use income alone here — the Roth conversion amount is excluded from both. This reverses an
   * earlier rule where the conversion counted toward the bracket; it no longer does, for these
   * two rates specifically. (Federal tax rate and the "until next bracket" hint were NOT
   * included in this change and still use combined income — see getIncomeForBrackets.)
   */
  private investmentRateForIncome(taxRates: TaxRates, income: number): number | null {
    const bracket = this.bracketFor(taxRates.federalCapitalGainsRateBrackets, income);
    if (!bracket) {
      return null;
    }
    const niit = taxRates.netInvestmentIncomeTax;
    const rate = bracket.rate + (income >= niit.thresholdIncome ? niit.rate : 0);
    // Rates arrive as ratios; the subjects these feed are on a 0-100 scale.
    return Number((rate * 100).toFixed(2));
  }

  // The three setters below deliberately skip setGraphData: they mirror fields that are already tracked by
  // their own setters, and recalculating from an error path would push a zeroed income into the graph and
  // BETR calc.
  setEffectiveCurrentIncome(effectiveCurrentIncome: number): void {
    this.effectiveCurrentIncomeSubject.next(effectiveCurrentIncome);
    this.recomputeCombinedIncome();
  }

  setEffectiveFutureIncome(effectiveFutureIncome: number): void {
    this.effectiveFutureIncomeSubject.next(effectiveFutureIncome);
    this.recomputeCombinedIncome();
  }

  setEffectiveRothConversionAmount(effectiveRothConversionAmount: number): void {
    this.effectiveRothConversionAmountSubject.next(effectiveRothConversionAmount);
    this.recomputeCombinedIncome();
  }

  getCombinedCurrentIncome$(): Observable<number> {
    return this.combinedCurrentIncomeSubject.asObservable();
  }

  getCombinedFutureIncome$(): Observable<number> {
    return this.combinedFutureIncomeSubject.asObservable();
  }

  setFutureFilingStatus(futureFilingStatus: string): void {
    this.futureFilingStatusSubject.next(futureFilingStatus);
    this.markEntered('futureFilingStatus');
    this.setTaxRates();
    this.setGraphData();
  }

  getFutureFilingStatus$(): Observable<string> {
    return this.futureFilingStatusSubject.asObservable();
  }

  setRothConversionAmount(rothConversionAmount: number): void {
    this.rothConversionAmountSubject.next(rothConversionAmount);
    this.markEntered('rothConversionAmount');
    this.setGraphData();
  }

  setFutureFederalTaxRate(futureFederalTaxRate: number): void {
    this.futureFederalTaxRateSubject.next(futureFederalTaxRate);
    this.markEntered('futureFederalTaxRate');
    this.setGraphData();
    this.setFutureTaxRate();
  }

  getFutureFederalTaxRate$(): Observable<number> {
    return this.futureFederalTaxRateSubject.asObservable();
  }

  setFutureStateTaxRate(futureStateTaxRate: number): void {
    this.futureStateTaxRateSubject.next(futureStateTaxRate);
    this.markEntered('futureStateTaxRate');
    this.setGraphData();
    this.setFutureTaxRate();
  }

  getFutureStateTaxRate$(): Observable<number> {
    return this.futureStateTaxRateSubject.asObservable();
  }

  setFutureStateOfTaxation(futureStateOfTaxation: string): void {
    this.futureStateOfTaxationSubject.next(futureStateOfTaxation);
    this.markEntered('futureStateOfTaxation');
    this.setTaxRates();
  }

  getFutureStateOfTaxation$(): Observable<string> {
    return this.futureStateOfTaxationSubject.asObservable();
  }

  setFutureStateTaxStatic(futureStateTaxStatic: number): void {
    this.futureStateTaxStaticSubject.next(futureStateTaxStatic);
    this.markEntered('futureStateTaxStatic');
  }

  getFutureStateTaxStatic$(): Observable<number> {
    return this.futureStateTaxStaticSubject.asObservable();
  }

  setFutureLocalTax(futureLocalTax: number): void {
    this.futureLocalTaxSubject.next(futureLocalTax);
    this.markEntered('futureLocalTax');
  }

  getFutureLocalTax$(): Observable<number> {
    return this.futureLocalTaxSubject.asObservable();
  }

  setFutureTaxRate(): void {
    this.futureTaxRateSubject.next(
      this.futureFederalTaxRateSubject.getValue() / 100 +
        this.futureStateTaxRateSubject.getValue() / 100,
    );
  }

  getFutureTaxRate$(): Observable<number> {
    return this.futureTaxRateSubject.asObservable();
  }

  getRothConversionAmount$(): Observable<number> {
    return this.rothConversionAmountSubject.asObservable();
  }

  setYearsUntilDist(yearsUntilDist: number): void {
    this.yearsUntilDistSubject.next(yearsUntilDist);
    this.markEntered('yearsUntilDist');
    this.setGraphData();
  }

  getYearsUntilDist$(): Observable<number> {
    return this.yearsUntilDistSubject.asObservable();
  }

  setHowWillYouPay(howWillYouPay: string): void {
    this.howWillYouPaySubject.next(howWillYouPay);
    this.markEntered('howWillYouPay');
    this.setGraphData();
  }

  getHowWillYouPay$(): Observable<string> {
    return this.howWillYouPaySubject.asObservable();
  }

  setPercentOfIra(percentOfIra: number): void {
    this.percentOfIraSubject.next(percentOfIra);
    this.markEntered('percentOfIra');
    this.setGraphData();
  }

  getPercentOfIra$(): Observable<number> {
    return this.percentOfIraSubject.asObservable();
  }

  getDividendTaxRate$(): Observable<number> {
    return this.dividendTaxRateSubject.asObservable();
  }

  setDividendTaxRate(dividendTaxRate: number): void {
    this.dividendTaxRateSubject.next(dividendTaxRate);
    this.setGraphData();
  }

  getCapitalGainsTaxRate$(): Observable<number> {
    return this.capitalGainsTaxRateSubject.asObservable();
  }

  setCapitalGainsTaxRate(capitalGainsTaxRate: number): void {
    this.capitalGainsTaxRateSubject.next(capitalGainsTaxRate);
    this.setGraphData();
  }

  getAnnualReturn$(): Observable<number> {
    return this.annualReturnSubject.asObservable();
  }

  setAnnualReturn(annualReturn: number): void {
    this.annualReturnSubject.next(annualReturn);
    this.markEntered('annualReturn');
    this.setGraphData();
  }

  getDividendYield$(): Observable<number> {
    return this.dividendYieldSubject.asObservable();
  }

  setDividendYield(dividendYield: number): void {
    this.dividendYieldSubject.next(dividendYield);
    this.setGraphData();
  }

  getCashYield$(): Observable<number> {
    return this.cashYieldSubject.asObservable();
  }

  setCashYield(cashYield: number): void {
    this.cashYieldSubject.next(cashYield);
    this.setGraphData();
  }

  getTaxChartDividendTaxRate$(): Observable<number> {
    return this.taxChartDividendTaxRateSubject.asObservable();
  }

  setTaxChartDividendTaxRate(taxChartDividendTaxRate: number): void {
    this.taxChartDividendTaxRateSubject.next(taxChartDividendTaxRate);
    this.setGraphData();
  }

  getTaxChartCapitalGainsTaxRate$(): Observable<number> {
    return this.taxChartCapitalGainsTaxRateSubject.asObservable();
  }

  setTaxChartCapitalGainsTaxRate(taxChartCapitalGainsTaxRate: number): void {
    this.taxChartCapitalGainsTaxRateSubject.next(taxChartCapitalGainsTaxRate);
    this.setGraphData();
  }

  get inputDetails(): InputDetails {
    return {
      currentAnnualIncome: this.currentIncomeSubject.getValue(),
      filingStatus: this.filingStatusSubject.getValue(),
      currentTaxRate:
        this.currentFederalTaxRateSubject.getValue() / 100 +
        this.currentStateTaxRateSubject.getValue() / 100,
      futureIncome: this.futureIncomeSubject.getValue(),
      futureFilingStatus: this.futureFilingStatusSubject.getValue(),
      futureTaxRate:
        this.futureFederalTaxRateSubject.getValue() / 100 +
        this.futureStateTaxRateSubject.getValue() / 100,

      rothConvAmt: this.rothConversionAmountSubject.getValue(),
      yearsUntilDist: this.yearsUntilDistSubject.getValue(),
      conversionTaxes: this.howWillYouPaySubject.getValue(),
      iraBasis: this.percentOfIraSubject.getValue() / 100,

      dividendTaxRate: this.dividendTaxRateSubject.getValue() / 100,
      gainTaxRate: this.capitalGainsTaxRateSubject.getValue() / 100,
      annualReturn: this.annualReturnSubject.getValue() / 100,
      dividendYield: this.dividendYieldSubject.getValue() / 100,
      cashYield: this.cashYieldSubject.getValue() / 100,
    };
  }

  /** Records the TXS-derived default a rate field was filled with (percent), or null to forget it. */
  setRateDefault(field: RateField, value: number | null): void {
    this.rateDefaults[field] = value;
  }

  /**
   * The rate as a BXR override (ratio), or null when it still equals its default — BXR then
   * derives the rate itself. No known default (TXS not loaded, or a cleared field) means nothing
   * to compare against, so nothing is overridden.
   */
  private overrideFor(field: RateField, value: number): number | null {
    const defaultValue = this.rateDefaults[field];
    if (defaultValue === null || Number(value) === Number(defaultValue)) {
      return null;
    }
    return Number(value) / 100;
  }

  get betrRequest(): BetrCalculateRequest {
    return {
      accountType: this.accountTypeSubject.getValue(),
      conversionTaxes: this.howWillYouPaySubject.getValue(),
      rothConvAmt: this.rothConversionAmountSubject.getValue(),
      iraBasis: this.percentOfIraSubject.getValue() / 100,
      annualReturn: this.annualReturnSubject.getValue() / 100,
      dividendYield: this.dividendYieldSubject.getValue() / 100,
      cashYield: this.cashYieldSubject.getValue() / 100,
      dividendTaxRate: this.dividendTaxRateSubject.getValue() / 100,
      gainTaxRate: this.capitalGainsTaxRateSubject.getValue() / 100,
      income: this.currentIncomeSubject.getValue(),
      filingStatus: this.filingStatusSubject.getValue(),
      state: this.currentStateOfTaxationSubject.getValue(),
      localTaxRate: this.currentLocalTaxSubject.getValue() / 100,
      futureIncome: this.futureIncomeSubject.getValue(),
      futureFilingStatus: this.futureFilingStatusSubject.getValue(),
      futureState: this.futureStateOfTaxationSubject.getValue(),
      futureLocalTaxRate: this.futureLocalTaxSubject.getValue() / 100,
      // State overrides compare the state rate alone; BXR takes local separately.
      currentFederalOverride: this.overrideFor(
        'currentFederal',
        this.currentFederalTaxRateSubject.getValue(),
      ),
      currentStateOverride: this.overrideFor(
        'currentState',
        this.currentStateTaxStaticSubject.getValue(),
      ),
      futureFederalOverride: this.overrideFor(
        'futureFederal',
        this.futureFederalTaxRateSubject.getValue(),
      ),
      futureStateOverride: this.overrideFor(
        'futureState',
        this.futureStateTaxStaticSubject.getValue(),
      ),
    };
  }

  get inputsValid() {
    return this.inputsErrorList.length === 0;
  }

  addInputError(errorNameString: string) {
    if (!this.inputsErrorList.find((errorString) => errorString === errorNameString)) {
      this.inputsErrorList.push(errorNameString);
      this.inputsValidSubject.next(this.inputsValid);
    }
  }

  removeInputError(errorNameString: string) {
    const index = this.inputsErrorList.findIndex((errorString) => errorString === errorNameString);
    if (index !== -1) {
      this.inputsErrorList.splice(index, 1);
      this.inputsValidSubject.next(this.inputsValid);
    }
  }

  /** False while any field is blank from Clear all fields, which holds the chart empty. */
  get inputsComplete(): boolean {
    return this.inputsCompleteSubject.getValue();
  }

  getInputsComplete$(): Observable<boolean> {
    return this.inputsCompleteSubject.pipe(distinctUntilChanged());
  }

  /** Emits the section each time its Clear all fields button is used. */
  getInputsCleared$(): Observable<InputSection> {
    return this.inputsClearedSubject.asObservable();
  }

  /**
   * Blanks every field in one input section without touching the other. The subjects keep their last
   * values - nothing reads a cleared field until it is re-entered, because the chart is held empty -
   * so a field is never stored as a 0 the user didn't type. The section's errors go with it: a blank
   * field after a clear is not a validation failure.
   */
  clearSection(section: InputSection): void {
    for (const input of this.sectionInputs(section)) {
      this.clearedInputs.add(input);
    }

    const sectionErrors = SECTION_INPUT_ERRORS[section];
    this.inputsErrorList = this.inputsErrorList.filter((name) => !sectionErrors.includes(name));
    this.inputsValidSubject.next(this.inputsValid);

    this.resetDerivedInputs(section);

    this.awaitingRecalculation = true;
    this.publishInputsComplete();
    this.sampleClientModifiedSubject.next(true);
    this.inputsClearedSubject.next(section);
  }

  private resetDerivedInputs(section: InputSection): void {
    // Blank income and conversion amount count as zero toward brackets and the combined-income hint,
    // the same as a blank field typed by hand.
    if (section === 'clientTax') {
      this.effectiveCurrentIncomeSubject.next(0);
      this.effectiveFutureIncomeSubject.next(0);
      // The brackets belong to the cleared filing status and state. Until each scenario's lookup runs
      // again, rates come from the year tables instead.
      this.currentTaxRatesSubject.next(null);
      this.futureTaxRatesSubject.next(null);
    } else {
      this.effectiveRothConversionAmountSubject.next(0);
    }
    this.recomputeCombinedIncome();
  }

  private sectionInputs(section: InputSection): ClearableInput[] {
    if (section === 'clientTax') {
      const scenarioInputs: ClearableInput[] = [
        'currentIncome',
        'currentFilingStatus',
        'currentFederalTaxRate',
        'futureIncome',
        'futureFilingStatus',
        'futureFederalTaxRate',
      ];
      // Behind the toggle the combined rate is split into state, static and local fields; without
      // it, the combined rate is the only state field on screen.
      const stateInputs: ClearableInput[] = this.gatekeeperService.getToggleValue('STATE_TAX_RATES')
        ? [
            'currentStateOfTaxation',
            'currentStateTaxStatic',
            'currentLocalTax',
            'futureStateOfTaxation',
            'futureStateTaxStatic',
            'futureLocalTax',
          ]
        : ['currentStateTaxRate', 'futureStateTaxRate'];
      return [...scenarioInputs, ...stateInputs];
    }
    // The payment method's conditional fields aren't listed: they're hidden while it is blank, and
    // picking a method shows them with their defaults.
    return ['rothConversionAmount', 'yearsUntilDist', 'howWillYouPay', 'annualReturn', 'percentOfIra'];
  }

  isInputCleared(input: ClearableInput): boolean {
    return this.clearedInputs.has(input);
  }

  /**
   * Whether a scenario's default federal rate can be derived. After a clear, the income and filing
   * status subjects still hold the pre-clear values - deriving from them would fill the field with a
   * rate for inputs the user no longer has. The state isn't needed: until its lookup lands the rate
   * comes from the year tables, and the lookup corrects it.
   */
  canDeriveFederalRate(isFuture: boolean): boolean {
    const income: ClearableInput = isFuture ? 'futureIncome' : 'currentIncome';
    const filingStatus: ClearableInput = isFuture ? 'futureFilingStatus' : 'currentFilingStatus';
    return !this.isInputCleared(income) && !this.isInputCleared(filingStatus);
  }

  // Per scenario, so filling in one scenario's dropdowns after a clear doesn't wait on the other's.
  private taxLookupCleared(isFuture: boolean): boolean {
    return isFuture
      ? this.isInputCleared('futureFilingStatus') || this.isInputCleared('futureStateOfTaxation')
      : this.isInputCleared('currentFilingStatus') || this.isInputCleared('currentStateOfTaxation');
  }

  // A cleared filing status or state has nothing to look up; it runs again once both are picked.
  private fetchTaxRates(isFuture: boolean): Promise<TaxRates | null> {
    if (this.taxLookupCleared(isFuture)) {
      return Promise.resolve(null);
    }
    return isFuture
      ? this.taxRatesService.fetch(
          this.futureFilingStatusSubject.getValue(),
          this.futureStateOfTaxationSubject.getValue(),
        )
      : this.taxRatesService.fetch(
          this.filingStatusSubject.getValue(),
          this.currentStateOfTaxationSubject.getValue(),
        );
  }

  private markEntered(input: ClearableInput): void {
    if (this.clearedInputs.delete(input) && this.clearedInputs.size === 0) {
      // The chart stays held until the recalculation from these values lands.
      this.graphDataNowTrigger.next();
    }
  }

  private publishInputsComplete(): void {
    this.inputsCompleteSubject.next(this.clearedInputs.size === 0 && !this.awaitingRecalculation);
  }

  //2025 tax rate functions

  // eslint-disable-next-line complexity,max-statements
  getAmountUntilNextTaxBracket2025() {
    let amountUntilNextTaxBracket: number | null = null;
    const income = this.getIncomeForBrackets(false);
    const filingStatus = this.filingStatusSubject.getValue();

    switch (filingStatus) {
      case 'single':
        if (income <= 11925) {
          amountUntilNextTaxBracket = 11926 - income;
        } else if (income <= 48475) {
          amountUntilNextTaxBracket = 48476 - income;
        } else if (income <= 103350) {
          amountUntilNextTaxBracket = 103351 - income;
        } else if (income <= 197300) {
          amountUntilNextTaxBracket = 197301 - income;
        } else if (income <= 250525) {
          amountUntilNextTaxBracket = 250526 - income;
        } else if (income <= 626350) {
          amountUntilNextTaxBracket = 626351 - income;
        }
        break;
      case 'married-filing-jointly':
        if (income <= 23850) {
          amountUntilNextTaxBracket = 23851 - income;
        } else if (income <= 96950) {
          amountUntilNextTaxBracket = 96951 - income;
        } else if (income <= 206700) {
          amountUntilNextTaxBracket = 206701 - income;
        } else if (income <= 394600) {
          amountUntilNextTaxBracket = 394601 - income;
        } else if (income <= 501050) {
          amountUntilNextTaxBracket = 501051 - income;
        } else if (income <= 751600) {
          amountUntilNextTaxBracket = 751601 - income;
        }
        break;
      case 'head-of-household':
        if (income <= 17000) {
          amountUntilNextTaxBracket = 17001 - income;
        } else if (income <= 64850) {
          amountUntilNextTaxBracket = 64851 - income;
        } else if (income <= 103350) {
          amountUntilNextTaxBracket = 103351 - income;
        } else if (income <= 197300) {
          amountUntilNextTaxBracket = 197301 - income;
        } else if (income <= 250500) {
          amountUntilNextTaxBracket = 250501 - income;
        } else if (income <= 626350) {
          amountUntilNextTaxBracket = 626351 - income;
        }
        break;
      case 'married-filing-separately':
        if (income <= 11925) {
          amountUntilNextTaxBracket = 11926 - income;
        } else if (income <= 48475) {
          amountUntilNextTaxBracket = 48476 - income;
        } else if (income <= 103350) {
          amountUntilNextTaxBracket = 103351 - income;
        } else if (income <= 197300) {
          amountUntilNextTaxBracket = 197301 - income;
        } else if (income <= 250525) {
          amountUntilNextTaxBracket = 250526 - income;
        } else if (income <= 375800) {
          amountUntilNextTaxBracket = 375801 - income;
        }
        break;
    }
    return amountUntilNextTaxBracket;
  }

  // eslint-disable-next-line max-statements
  get2025TaxRate(isFuture: boolean): number {
    let newTaxRate = 0;

    const income = this.getIncomeForBrackets(isFuture);
    const filingStatus = isFuture
      ? this.futureFilingStatusSubject.getValue()
      : this.filingStatusSubject.getValue();

    switch (filingStatus) {
      case 'single':
        if (income <= 11925) {
          newTaxRate = 10;
        } else if (income <= 48475) {
          newTaxRate = 12;
        } else if (income <= 103350) {
          newTaxRate = 22;
        } else if (income <= 197300) {
          newTaxRate = 24;
        } else if (income <= 250525) {
          newTaxRate = 32;
        } else if (income <= 626350) {
          newTaxRate = 35;
        } else {
          newTaxRate = 37;
        }
        break;
      case 'married-filing-jointly':
        if (income <= 23850) {
          newTaxRate = 10;
        } else if (income <= 96950) {
          newTaxRate = 12;
        } else if (income <= 206700) {
          newTaxRate = 22;
        } else if (income <= 394600) {
          newTaxRate = 24;
        } else if (income <= 501050) {
          newTaxRate = 32;
        } else if (income <= 751600) {
          newTaxRate = 35;
        } else {
          newTaxRate = 37;
        }
        break;
      case 'head-of-household':
        if (income <= 17000) {
          newTaxRate = 10;
        } else if (income <= 64850) {
          newTaxRate = 12;
        } else if (income <= 103350) {
          newTaxRate = 22;
        } else if (income <= 197300) {
          newTaxRate = 24;
        } else if (income <= 250500) {
          newTaxRate = 32;
        } else if (income <= 626350) {
          newTaxRate = 35;
        } else {
          newTaxRate = 37;
        }
        break;
      case 'married-filing-separately':
        if (income <= 11925) {
          newTaxRate = 10;
        } else if (income <= 48475) {
          newTaxRate = 12;
        } else if (income <= 103350) {
          newTaxRate = 22;
        } else if (income <= 197300) {
          newTaxRate = 24;
        } else if (income <= 250525) {
          newTaxRate = 32;
        } else if (income <= 375800) {
          newTaxRate = 35;
        } else {
          newTaxRate = 37;
        }
        break;
    }
    return newTaxRate;
  }

  // eslint-disable-next-line max-statements
  setUpdatedDividendTaxRate2025() {
    let cai: any = null;
    this.currentIncomeSubject.pipe(take(1)).subscribe((value) => {
      cai = value;
    });
    let filingStatus: any = null;
    this.filingStatusSubject.pipe(take(1)).subscribe((value) => {
      filingStatus = value;
    });
    switch (filingStatus) {
      case 'single':
        if (cai <= 48350) {
          this.setTaxChartDividendTaxRate(0);
        } else if (cai <= 199999) {
          this.setTaxChartDividendTaxRate(15);
        } else if (cai <= 533400) {
          this.setTaxChartDividendTaxRate(18.8);
        } else {
          this.setTaxChartDividendTaxRate(23.8);
        }
        break;
      case 'married-filing-jointly':
        if (cai <= 96700) {
          this.setTaxChartDividendTaxRate(0);
        } else if (cai <= 249999) {
          this.setTaxChartDividendTaxRate(15);
        } else if (cai <= 600050) {
          this.setTaxChartDividendTaxRate(18.8);
        } else {
          this.setTaxChartDividendTaxRate(23.8);
        }
        break;
      case 'head-of-household':
        if (cai <= 64750) {
          this.setTaxChartDividendTaxRate(0);
        } else if (cai <= 199999) {
          this.setTaxChartDividendTaxRate(15);
        } else if (cai <= 566700) {
          this.setTaxChartDividendTaxRate(18.8);
        } else {
          this.setTaxChartDividendTaxRate(23.8);
        }
        break;
      case 'married-filing-separately':
        if (cai <= 48350) {
          this.setTaxChartDividendTaxRate(0);
        } else if (cai <= 124999) {
          this.setTaxChartDividendTaxRate(15);
        } else if (cai <= 300000) {
          this.setTaxChartDividendTaxRate(18.8);
        } else {
          this.setTaxChartDividendTaxRate(23.8);
        }
        break;
    }
  }

  // eslint-disable-next-line max-statements
  setUpdatedCapitalGainsTaxRate2025() {
    let fai: any = null;
    this.futureIncomeSubject.pipe(take(1)).subscribe((value) => {
      fai = value;
    });
    let futureFilingStatus: any = null;
    this.futureFilingStatusSubject.pipe(take(1)).subscribe((value) => {
      futureFilingStatus = value;
    });
    switch (futureFilingStatus) {
      case 'single':
        if (fai <= 48350) {
          this.setTaxChartCapitalGainsTaxRate(0);
        } else if (fai <= 199999) {
          this.setTaxChartCapitalGainsTaxRate(15);
        } else if (fai <= 533400) {
          this.setTaxChartCapitalGainsTaxRate(18.8);
        } else {
          this.setTaxChartCapitalGainsTaxRate(23.8);
        }
        break;
      case 'married-filing-jointly':
        if (fai <= 96700) {
          this.setTaxChartCapitalGainsTaxRate(0);
        } else if (fai <= 249999) {
          this.setTaxChartCapitalGainsTaxRate(15);
        } else if (fai <= 600050) {
          this.setTaxChartCapitalGainsTaxRate(18.8);
        } else {
          this.setTaxChartCapitalGainsTaxRate(23.8);
        }
        break;
      case 'head-of-household':
        if (fai <= 64750) {
          this.setTaxChartCapitalGainsTaxRate(0);
        } else if (fai <= 199999) {
          this.setTaxChartCapitalGainsTaxRate(15);
        } else if (fai <= 566700) {
          this.setTaxChartCapitalGainsTaxRate(18.8);
        } else {
          this.setTaxChartCapitalGainsTaxRate(23.8);
        }
        break;
      case 'married-filing-separately':
        if (fai <= 48350) {
          this.setTaxChartCapitalGainsTaxRate(0);
        } else if (fai <= 124999) {
          this.setTaxChartCapitalGainsTaxRate(15);
        } else if (fai <= 300000) {
          this.setTaxChartCapitalGainsTaxRate(18.8);
        } else {
          this.setTaxChartCapitalGainsTaxRate(23.8);
        }
        break;
    }
  }

  // The five dispatchers below prefer live TXS brackets and fall back to the hardcoded
  // year tables when none are loaded — which is the case behind the STATE_TAX_RATES
  // toggle (no fetch is made) and before the first fetch resolves. The fallback is what
  // keeps the toggle a working rollback switch, so it stays until the toggle is retired.
  private taxRateFromBrackets(isFuture: boolean): number | null {
    const taxRates = this.taxRatesFor(isFuture);
    if (!taxRates) {
      return null;
    }
    const bracket = this.bracketFor(
      taxRates.federalIncomeTaxRateBrackets,
      this.getIncomeForBrackets(isFuture),
    );
    // Rates arrive as ratios; callers expect a 0-100 percentage.
    return bracket ? Number((bracket.rate * 100).toFixed(2)) : null;
  }

  /**
   * Default state rate: the TXS state bracket the income falls in, as a percentage. CURRENT adds
   * the conversion only when the state taxes Roth conversions; FUTURE uses future income alone
   * (confirmed with the business 2026-10-13). Null until brackets are loaded, so the field keeps
   * what it showed — which is always the case behind the STATE_TAX_RATES toggle.
   */
  getStateTaxRateBasedOnIncome(isFuture: boolean): number | null {
    const taxRates = this.taxRatesFor(isFuture);
    if (!taxRates) {
      return null;
    }
    const income =
      !isFuture && taxRates.stateTaxesRothConversion
        ? this.getIncomeForBrackets(false)
        : this.getIncomeExcludingConversion(isFuture);
    // No brackets means the state has no income tax.
    const bracket = this.bracketFor(taxRates.stateIncomeTaxRateBrackets, income);
    return bracket ? Number((bracket.rate * 100).toFixed(2)) : 0;
  }

  getCurrentTaxRateBasedOnCurrentYear(): number {
    const rate = this.taxRateFromBrackets(false);
    if (rate !== null) {
      return rate;
    }
    switch (new Date().getFullYear()) {
      case 2025:
        return this.get2025TaxRate(false);
      case 2026:
        return this.get2026TaxRate(false);
      default:
        return this.get2025TaxRate(false);
    }
  }

  getFutureTaxRateBasedOnCurrentYear(): number {
    const rate = this.taxRateFromBrackets(true);
    if (rate !== null) {
      return rate;
    }
    switch (new Date().getFullYear()) {
      case 2025:
        return this.get2025TaxRate(true);
      case 2026:
        return this.get2026TaxRate(true);
      default:
        return this.get2025TaxRate(true);
    }
  }

  getAmountUntilNextTaxBracketBasedOnCurrentYear() {
    const taxRates = this.taxRatesFor(false);
    if (taxRates) {
      return this.amountUntilNextBracket(
        taxRates.federalIncomeTaxRateBrackets,
        this.getIncomeForBrackets(false),
      );
    }
    switch (new Date().getFullYear()) {
      case 2025:
        return this.getAmountUntilNextTaxBracket2025();
      case 2026:
        return this.getAmountUntilNextTaxBracket2026();
      default:
        return this.getAmountUntilNextTaxBracket2025();
    }
  }

  // Dividend rate keys off CURRENT income — dividends are taxed while the account is held.
  setUpdatedDividendTaxRateBasedOnCurrentYear() {
    const taxRates = this.taxRatesFor(false);
    if (taxRates) {
      const rate = this.investmentRateForIncome(taxRates, this.getIncomeExcludingConversion(false));
      if (rate !== null) {
        this.setTaxChartDividendTaxRate(rate);
        return;
      }
    }
    switch (new Date().getFullYear()) {
      case 2025:
        this.setUpdatedDividendTaxRate2025();
        break;
      case 2026:
        this.setUpdatedDividendTaxRate2026();
        break;
      default:
        this.setUpdatedDividendTaxRate2025();
    }
  }

  // Capital gains keys off FUTURE income — gains are taxed at distribution, not today.
  setUpdatedCapitalGainsTaxRateBasedOnCurrentYear() {
    const taxRates = this.taxRatesFor(true);
    if (taxRates) {
      const rate = this.investmentRateForIncome(taxRates, this.getIncomeExcludingConversion(true));
      if (rate !== null) {
        this.setTaxChartCapitalGainsTaxRate(rate);
        return;
      }
    }
    switch (new Date().getFullYear()) {
      case 2025:
        this.setUpdatedCapitalGainsTaxRate2025();
        break;
      case 2026:
        this.setUpdatedCapitalGainsTaxRate2026();
        break;
      default:
        this.setUpdatedCapitalGainsTaxRate2025();
    }
  }

  // 2026 tax rate functions

  // ||||||||||||||||| HAS BEEN UPDATED BASED ON 2026 TAX RATES |||||||||||||||||
  // eslint-disable-next-line complexity,max-statements
  getAmountUntilNextTaxBracket2026() {
    let amountUntilNextTaxBracket: number | null = null;
    const income = this.getIncomeForBrackets(false);
    const filingStatus = this.filingStatusSubject.getValue();

    switch (filingStatus) {
      case 'single':
        if (income <= 12400) {
          amountUntilNextTaxBracket = 12401 - income;
        } else if (income <= 50400) {
          amountUntilNextTaxBracket = 50401 - income;
        } else if (income <= 105700) {
          amountUntilNextTaxBracket = 105701 - income;
        } else if (income <= 201775) {
          amountUntilNextTaxBracket = 201776 - income;
        } else if (income <= 256225) {
          amountUntilNextTaxBracket = 256226 - income;
        } else if (income <= 640600) {
          amountUntilNextTaxBracket = 640601 - income;
        }
        break;
      case 'married-filing-jointly':
        if (income <= 24800) {
          amountUntilNextTaxBracket = 24801 - income;
        } else if (income <= 100800) {
          amountUntilNextTaxBracket = 100801 - income;
        } else if (income <= 211400) {
          amountUntilNextTaxBracket = 211401 - income;
        } else if (income <= 403550) {
          amountUntilNextTaxBracket = 403551 - income;
        } else if (income <= 512450) {
          amountUntilNextTaxBracket = 512451 - income;
        } else if (income <= 768700) {
          amountUntilNextTaxBracket = 768701 - income;
        }
        break;
      case 'head-of-household':
        if (income <= 17700) {
          amountUntilNextTaxBracket = 17701 - income;
        } else if (income <= 67450) {
          amountUntilNextTaxBracket = 67451 - income;
        } else if (income <= 105700) {
          amountUntilNextTaxBracket = 105701 - income;
        } else if (income <= 201750) {
          amountUntilNextTaxBracket = 201751 - income;
        } else if (income <= 256200) {
          amountUntilNextTaxBracket = 256201 - income;
        } else if (income <= 640600) {
          amountUntilNextTaxBracket = 640601 - income;
        }
        break;
      case 'married-filing-separately':
        if (income <= 12400) {
          amountUntilNextTaxBracket = 12401 - income;
        } else if (income <= 50400) {
          amountUntilNextTaxBracket = 50401 - income;
        } else if (income <= 105700) {
          amountUntilNextTaxBracket = 105701 - income;
        } else if (income <= 201775) {
          amountUntilNextTaxBracket = 201776 - income;
        } else if (income <= 256225) {
          amountUntilNextTaxBracket = 256226 - income;
        } else if (income <= 384350) {
          amountUntilNextTaxBracket = 384351 - income;
        }
        break;
    }
    return amountUntilNextTaxBracket;
  }

  // ||||||||||||||||| HAS BEEN UPDATED BASED ON 2026 TAX RATES |||||||||||||||||
  // eslint-disable-next-line max-statements
  setUpdatedDividendTaxRate2026() {
    let cai: any = null;
    this.currentIncomeSubject.pipe(take(1)).subscribe((value) => {
      cai = value;
    });
    let filingStatus: any = null;
    this.filingStatusSubject.pipe(take(1)).subscribe((value) => {
      filingStatus = value;
    });
    switch (filingStatus) {
      case 'single':
        if (cai <= 49450) {
          this.setTaxChartDividendTaxRate(0);
        } else if (cai <= 199999) {
          this.setTaxChartDividendTaxRate(15);
        } else if (cai <= 545500) {
          this.setTaxChartDividendTaxRate(18.8);
        } else {
          this.setTaxChartDividendTaxRate(23.8);
        }
        break;
      case 'married-filing-jointly':
        if (cai <= 98900) {
          this.setTaxChartDividendTaxRate(0);
        } else if (cai <= 249999) {
          this.setTaxChartDividendTaxRate(15);
        } else if (cai <= 613700) {
          this.setTaxChartDividendTaxRate(18.8);
        } else {
          this.setTaxChartDividendTaxRate(23.8);
        }
        break;
      case 'head-of-household':
        if (cai <= 66200) {
          this.setTaxChartDividendTaxRate(0);
        } else if (cai <= 199999) {
          this.setTaxChartDividendTaxRate(15);
        } else if (cai <= 579600) {
          this.setTaxChartDividendTaxRate(18.8);
        } else {
          this.setTaxChartDividendTaxRate(23.8);
        }
        break;
      case 'married-filing-separately':
        if (cai <= 49450) {
          this.setTaxChartDividendTaxRate(0);
        } else if (cai <= 124999) {
          this.setTaxChartDividendTaxRate(15);
        } else if (cai <= 306850) {
          this.setTaxChartDividendTaxRate(18.8);
        } else {
          this.setTaxChartDividendTaxRate(23.8);
        }
        break;
    }
  }

  // ||||||||||||||||| HAS BEEN UPDATED BASED ON 2026 TAX RATES |||||||||||||||||
  // eslint-disable-next-line max-statements
  setUpdatedCapitalGainsTaxRate2026() {
    let fai: any = null;
    this.futureIncomeSubject.pipe(take(1)).subscribe((value) => {
      fai = value;
    });
    let futureFilingStatus: any = null;
    this.futureFilingStatusSubject.pipe(take(1)).subscribe((value) => {
      futureFilingStatus = value;
    });
    switch (futureFilingStatus) {
      case 'single':
        if (fai <= 49450) {
          this.setTaxChartCapitalGainsTaxRate(0);
        } else if (fai <= 199999) {
          this.setTaxChartCapitalGainsTaxRate(15);
        } else if (fai <= 545500) {
          this.setTaxChartCapitalGainsTaxRate(18.8);
        } else {
          this.setTaxChartCapitalGainsTaxRate(23.8);
        }
        break;
      case 'married-filing-jointly':
        if (fai <= 98900) {
          this.setTaxChartCapitalGainsTaxRate(0);
        } else if (fai <= 249999) {
          this.setTaxChartCapitalGainsTaxRate(15);
        } else if (fai <= 613700) {
          this.setTaxChartCapitalGainsTaxRate(18.8);
        } else {
          this.setTaxChartCapitalGainsTaxRate(23.8);
        }
        break;
      case 'head-of-household':
        if (fai <= 66200) {
          this.setTaxChartCapitalGainsTaxRate(0);
        } else if (fai <= 199999) {
          this.setTaxChartCapitalGainsTaxRate(15);
        } else if (fai <= 579600) {
          this.setTaxChartCapitalGainsTaxRate(18.8);
        } else {
          this.setTaxChartCapitalGainsTaxRate(23.8);
        }
        break;
      case 'married-filing-separately':
        if (fai <= 49450) {
          this.setTaxChartCapitalGainsTaxRate(0);
        } else if (fai <= 124999) {
          this.setTaxChartCapitalGainsTaxRate(15);
        } else if (fai <= 306850) {
          this.setTaxChartCapitalGainsTaxRate(18.8);
        } else {
          this.setTaxChartCapitalGainsTaxRate(23.8);
        }
        break;
    }
  }

  // eslint-disable-next-line max-statements
  get2026TaxRate(isFuture: boolean): number {
    let newTaxRate = 0;

    const income = this.getIncomeForBrackets(isFuture);
    const filingStatus = isFuture
      ? this.futureFilingStatusSubject.getValue()
      : this.filingStatusSubject.getValue();

    switch (filingStatus) {
      case 'single':
        if (income <= 12400) {
          newTaxRate = 10;
        } else if (income <= 50400) {
          newTaxRate = 12;
        } else if (income <= 105700) {
          newTaxRate = 22;
        } else if (income <= 201775) {
          newTaxRate = 24;
        } else if (income <= 256225) {
          newTaxRate = 32;
        } else if (income <= 640600) {
          newTaxRate = 35;
        } else {
          newTaxRate = 37;
        }
        break;
      case 'married-filing-jointly':
        if (income <= 24800) {
          newTaxRate = 10;
        } else if (income <= 100800) {
          newTaxRate = 12;
        } else if (income <= 211400) {
          newTaxRate = 22;
        } else if (income <= 403550) {
          newTaxRate = 24;
        } else if (income <= 512450) {
          newTaxRate = 32;
        } else if (income <= 768700) {
          newTaxRate = 35;
        } else {
          newTaxRate = 37;
        }
        break;
      case 'head-of-household':
        if (income <= 17700) {
          newTaxRate = 10;
        } else if (income <= 67450) {
          newTaxRate = 12;
        } else if (income <= 105700) {
          newTaxRate = 22;
        } else if (income <= 201750) {
          newTaxRate = 24;
        } else if (income <= 256200) {
          newTaxRate = 32;
        } else if (income <= 640600) {
          newTaxRate = 35;
        } else {
          newTaxRate = 37;
        }
        break;
      case 'married-filing-separately':
        if (income <= 12400) {
          newTaxRate = 10;
        } else if (income <= 50400) {
          newTaxRate = 12;
        } else if (income <= 105700) {
          newTaxRate = 22;
        } else if (income <= 201775) {
          newTaxRate = 24;
        } else if (income <= 256225) {
          newTaxRate = 32;
        } else if (income <= 384350) {
          newTaxRate = 35;
        } else {
          newTaxRate = 37;
        }
        break;
    }
    return newTaxRate;
  }

  // The cert modal's Login link leaves the page, so the scenario is held in sessionStorage for login flow
  private static readonly LOGIN_SCENARIO_KEY = 'rccLoginScenario';

  saveScenarioForLogin(): void {
    sessionStorage.setItem(
      SharedInfoService.LOGIN_SCENARIO_KEY,
      JSON.stringify({
        inputs: this.currentInputData,
        sampleClientIndex: this.getSampleClientIndex(),
      }),
    );
  }

  // Removed as soon as it is read, so the client's figures don't outlive the  login flow
  restoreScenarioAfterLogin(): boolean {
    const stored = sessionStorage.getItem(SharedInfoService.LOGIN_SCENARIO_KEY);
    sessionStorage.removeItem(SharedInfoService.LOGIN_SCENARIO_KEY);
    if (!stored) return false;
    try {
      const { inputs, sampleClientIndex } = JSON.parse(stored);
      // Written directly: setSampleClientIndex() would load that sample's figures over the advisor's.
      if (Number.isInteger(sampleClientIndex) && sampleClientIndex >= 0 && sampleClientIndex < this.sampleInputData.length) {
        this.sampleClientIndexSubject.next(sampleClientIndex);
      }
      this.applyInputData(inputs);
      // applyInputData() ends by announcing a sample client load, which marks the scenario unedited.
      // Re-check against the sample so the applied-sample banner only shows if nothing was changed.
      this.sampleClientModifiedSubject.next(this.hasModifiedSampleClientData());
      return true;
    } catch {
      return false;
    }
  }
}
